'use client';

import Head from 'next/head';
import { usePathname } from 'next/navigation';
import { FC } from 'react';

import { BrowserHelpers } from 'helpers/BrowserHelpers';

import { useHeaderState } from 'components/header/hooks/use-header-state';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';

export const DEFAULT_META_TITLE = 'Direct Energy Partners';
export const DEFAULT_META_DESCRIPTION = 'The DC Microgrid Platform Company';

const Metadata: FC<{
    title?: string;
    description?: string;
    image?: string;
}> = ({
    title,
    description = DEFAULT_META_DESCRIPTION,
    image = 'https://directenergypartners.com/opengraph-image.png',
}) => {
    const { name: workspaceName, logoUrl, faviconUrl, heroImageUrl, shareImageUrl } = useWorkspaceConfig();
    const { showResourceProNav } = useHeaderState();

    const resourceProImage = 'https://directenergypartners.com/opengraph_image_resourcepro.png';

    const appName = showResourceProNav ? 'RE+Source PRO' : workspaceName || DEFAULT_META_TITLE;
    const appAbbreviation = showResourceProNav
        ? 'RE+Source PRO'
        : workspaceName || (showResourceProNav ? 'RE+Source PRO' : 'DEP');
    const metaTitle = !title || title === appName ? appName : `${title} | ${appAbbreviation}`;
    const ogImage = shareImageUrl || heroImageUrl || (showResourceProNav ? resourceProImage : image);
    const iconUrl = faviconUrl || logoUrl;

    return (
        <Head>
            <title key="title">{metaTitle}</title>
            <meta key="ogTitle" property="og:title" content={metaTitle} />

            <meta key="description" name="description" content={description} />
            <meta key="ogDescription" property="og:description" content={description} />

            <meta key="ogImage" property="og:image" content={ogImage} />
            <meta key="ogImageType" property="og:image:type" content="image/png" />
            <meta key="ogImageWidth" property="og:image:width" content="1200" />
            <meta key="ogImageHeight" property="og:image:height" content="630" />

            {/* Keys dedupe the icons across nested Metadata instances; browsers prefer the last declared icon, so the SVG goes last. */}
            {iconUrl ? (
                <link key="icon" rel="icon" href={iconUrl} />
            ) : (
                <>
                    <link key="icon-png" rel="icon" href="/favicon.png" type="image/png" sizes="48x48" />
                    <link key="icon-svg" rel="icon" href="/favicon.svg" type="image/svg+xml" sizes="48x48" />
                </>
            )}

            {BrowserHelpers.isIOS() && (
                <meta name="viewport" content="width=device-width,  initial-scale=1.0, maximum-scale=1.0" />
            )}
        </Head>
    );
};

export { Metadata };
